import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import {
  programmeCurriculumService,
  ProgrammeCurriculumMutationPayload,
} from '@/services/programmeCurriculumService'
import { ProgrammeCurriculumModel } from '@/models/programme-curriculum.model'

export const programmeCurriculumKeys = {
  all: ['programmeCurriculums'] as const,
  lists: () => [...programmeCurriculumKeys.all, 'list'] as const,
  list: (programmeId: string) => [...programmeCurriculumKeys.lists(), programmeId] as const,
  detail: (programmeId: string, curriculumId: string) =>
    [...programmeCurriculumKeys.list(programmeId), curriculumId] as const,
}

export function useProgrammeCurriculums(programmeId: string | undefined) {
  return useQuery({
    queryKey: programmeId ? programmeCurriculumKeys.list(programmeId) : programmeCurriculumKeys.lists(),
    queryFn: async (): Promise<ProgrammeCurriculumModel[]> => {
      if (!programmeId) throw new Error('Programme ID is required')
      return programmeCurriculumService.getCurriculums(programmeId)
    },
    enabled: Boolean(programmeId),
    staleTime: 5 * 60 * 1000,
    gcTime: 10 * 60 * 1000,
  })
}

export function useCreateProgrammeCurriculum(programmeId: string | undefined) {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async (payload: ProgrammeCurriculumMutationPayload) => {
      if (!programmeId) throw new Error('Programme ID is required')
      return programmeCurriculumService.createCurriculum(programmeId, payload)
    },
    onSuccess: () => {
      if (!programmeId) return
      queryClient.invalidateQueries({ queryKey: programmeCurriculumKeys.list(programmeId) })
    },
  })
}

export function useUpdateProgrammeCurriculum(
  programmeId: string | undefined,
  curriculumId: string | undefined,
) {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async (payload: ProgrammeCurriculumMutationPayload) => {
      if (!programmeId || !curriculumId) throw new Error('Programme ID and curriculum ID are required')
      return programmeCurriculumService.updateCurriculum(programmeId, curriculumId, payload)
    },
    onSuccess: () => {
      if (!programmeId || !curriculumId) return
      queryClient.invalidateQueries({ queryKey: programmeCurriculumKeys.list(programmeId) })
      queryClient.invalidateQueries({
        queryKey: programmeCurriculumKeys.detail(programmeId, curriculumId),
      })
    },
  })
}

export function useDeleteProgrammeCurriculum(programmeId: string | undefined) {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async (curriculumId: string) => {
      if (!programmeId) throw new Error('Programme ID is required')
      return programmeCurriculumService.deleteCurriculum(programmeId, curriculumId)
    },
    onSuccess: (_, curriculumId) => {
      if (!programmeId) return
      queryClient.invalidateQueries({ queryKey: programmeCurriculumKeys.list(programmeId) })
      if (curriculumId) {
        queryClient.invalidateQueries({
          queryKey: programmeCurriculumKeys.detail(programmeId, curriculumId),
        })
      }
    },
  })
}
